import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { getStudentMonthlyDetails } from "../../api/attendanceReportApi";
import "./Attendance.css";

const periodApi = createLookupApi("periods");
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const daysInMonth = (year, monthName) => {
  const m = MONTHS.indexOf(monthName);
  return new Date(year, m + 1, 0).getDate();
};

/**
 * Direct port of digicampusbd's `student-attendance-report-monthly-section-details`
 * ("Attendance Monthly Details") — per-student day-by-day pivot for one
 * month: each cell "P"/"A"/"L", a weekly-holiday code, an institute/section
 * holiday's own short name, or "-". Confirmed via `monthlyStudentAttendanceSectionDetails`
 * that this merges real `StudentAttendance` rows with `WeeklyHoliday`/
 * `OthersHoliday` per day, exactly like the already-built exam-marksheet
 * attendance feature's holiday-exclusion logic — new `GET
 * /api/attendance/reports/student/monthly-details` reuses that same
 * `WeeklyHoliday`/`OthersHoliday` resolution, extended to per-day resolution
 * instead of a single total.
 */
export default function StudentMonthlyDetailsPage() {
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [monthName, setMonthName] = useState(MONTHS[new Date().getMonth()]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    periodApi.list().then((list) => {
      setPeriods(list);
      if (list.length > 0) setPeriodId(String(list[0].id));
    });
  }, []);

  const handleSearch = async () => {
    setError("");
    setLoading(true);
    try {
      setRows(await getStudentMonthlyDetails(classConfigId, periodId, monthName, year));
    } catch (err) {
      setError("Failed to load monthly attendance details.");
    } finally {
      setLoading(false);
    }
  };

  const dayCount = daysInMonth(year, monthName);
  const dayCols = Array.from({ length: dayCount }, (_, i) => String(i + 1));

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Attendance Monthly Details</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Period
          <select value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Month
          <select value={monthName} onChange={(e) => setMonthName(e.target.value)}>
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label>
          Year
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        rows && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Roll</th>
                  {dayCols.map((d) => (
                    <th key={d}>{d}</th>
                  ))}
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.customId}</td>
                    <td>{r.name}</td>
                    <td>{r.rollOrSerial}</td>
                    {dayCols.map((d) => (
                      <td key={d}>{r.days[d]}</td>
                    ))}
                    <td>{r.summary}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={dayCols.length + 4}>No students found for this selection.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
